import type { BillingBannerState, SubscriptionCardState } from '@parsleyai/ui'
import { graceDaysRemaining, trialDaysRemaining } from '@/pages/settings/subscriptionCardState'
import type { BillingSubscriptionDoc } from '@/store/userData/billing/types'

/**
 * Upper bounds for the trial banner: 7…4 days is `trial_t7`, 3…2 is `trial_t3`, 1…0 the last day.
 * `Ceil` means a trial ending in twelve hours reads as 1, so `0` only happens once the end has passed.
 */
const TRIAL_STEPS: Array<[maxDays: number, state: BillingBannerState]> = [
  [1, 'trial_t0'],
  [3, 'trial_t3'],
  [7, 'trial_t7']
]

/**
 * Map the subscription onto the banner's state. Derived from {@link SubscriptionCardState} rather than
 * re-reading `status`, so the banner and the settings card can never disagree about someone's money.
 */
export function toBannerState(card: SubscriptionCardState, sub: BillingSubscriptionDoc | null, nowMs: number): BillingBannerState {
  switch (card) {
    case 'past_due': {
      const since = sub?.pastDueSince?.toMillis()
      // No anchor means we can't say how long is left, so don't claim a number — the softer of the
      // two states is the honest one. `grace_expired` covers the other side of the line already.
      if (!since) return 'past_due'
      return graceDaysRemaining(since, nowMs) <= 1 ? 'grace_today' : 'past_due'
    }
    case 'grace_expired':
    case 'terminal':
      return 'locked'
    case 'paused':
      return 'paused'
    case 'trialing': {
      const end = sub?.trialEnd?.toMillis()
      if (!end) return 'none'
      const days = trialDaysRemaining(end, nowMs)
      return TRIAL_STEPS.find(([max]) => days <= max)?.[1] ?? 'none'
    }
    default:
      return 'none'
  }
}

/**
 * Whether billing claims the app's single banner slot, suppressing the connection banner beneath it.
 * Only the jeopardy states: a dropped socket self-heals in seconds, a dead card doesn't.
 */
export function bannerHoldsSlot(state: BillingBannerState): boolean {
  return state === 'past_due' || state === 'grace_today' || state === 'locked' || state === 'paused'
}

/** Whole days to put in the countdown tag, or `null` for the states that don't carry one. */
export function bannerCountdownDays(state: BillingBannerState, sub: BillingSubscriptionDoc | null, nowMs: number): number | null {
  if (state === 'past_due' || state === 'grace_today') {
    const since = sub?.pastDueSince?.toMillis()
    return since ? graceDaysRemaining(since, nowMs) : null
  }
  if (state.startsWith('trial_')) {
    const end = sub?.trialEnd?.toMillis()
    return end ? trialDaysRemaining(end, nowMs) : null
  }
  return null
}
